import { makeColumnFilterable, YColumnsType } from '@ydistri/ds';
import {
  ExecutionType,
  RouteExecutionStatus,
  SourceStoreEvaluationRatesResponse,
  TargetStoreEvaluationRatesResponse,
} from '@ydistri/api-sdk';
import { formatNumber, formatPercentage } from '@ydistri/generic-utils';
import { ColumnsType } from 'antd/es/table';
import { computeRemSize } from '@ydistri/theme';
import { YPICK_LABEL } from '../../../components/global/_constants';

export const evaluationsRowClassName = (
  tl: SourceStoreEvaluationRatesResponse | TargetStoreEvaluationRatesResponse,
): string => {
  switch (tl.evaluationSummary.status) {
    case RouteExecutionStatus.NotStarted:
      return 'red';
    case RouteExecutionStatus.InProgress:
      return 'orange';
    case RouteExecutionStatus.Finished:
      return 'green';
  }
};

const translatePickingProgressStatus = (status: RouteExecutionStatus) => {
  switch (status) {
    case RouteExecutionStatus.NotStarted:
      return 'Not Started';
    case RouteExecutionStatus.InProgress:
      return 'In progress';
    case RouteExecutionStatus.Finished:
      return 'Finished';
    default:
      return 'Unknown';
  }
};

const executionTypeLabelMap: Record<ExecutionType, string> = {
  [ExecutionType.PickAndPack]: YPICK_LABEL,
  [ExecutionType.Integration]: 'Integration',
  [ExecutionType.Heuristic]: 'Heuristic',
};

export const translateExecutionType = (executionType: ExecutionType): string => {
  return executionTypeLabelMap[executionType];
};

// Numeric values representing SendingRouteExecutionStatus for sorting
const pickingProgressStatusNumericValues = {
  [RouteExecutionStatus.NotStarted]: 0,
  [RouteExecutionStatus.InProgress]: 1,
  [RouteExecutionStatus.Finished]: 2,
};

/**
 * Compare two SendingRouteExecutionStatus values for sorting.
 * @param left
 * @param right
 * @return A negative number if left is less than right, a positive number if left is greater than right, or zero if they are equal.
 */
const comparePickingProgressStatus = (left: RouteExecutionStatus, right: RouteExecutionStatus) => {
  return pickingProgressStatusNumericValues[left] - pickingProgressStatusNumericValues[right];
};

export interface IncludeColumnsOptions {
  includeDepartments?: boolean;
  includeRoutes?: boolean;
  includeDeliveredValue?: boolean;
}

export const getBaseExecutionColumns = <
  T extends SourceStoreEvaluationRatesResponse | TargetStoreEvaluationRatesResponse,
>(
  currency: string,
  storeColumns: YColumnsType<T>[],
  includeColumnOptions: IncludeColumnsOptions = {
    includeDepartments: false,
    includeRoutes: false,
    includeDeliveredValue: false,
  },
  colWidth: number = 150,
): ColumnsType<T> => {
  const columns: ColumnsType<T> = [...storeColumns];
  const { includeDepartments, includeRoutes, includeDeliveredValue } = includeColumnOptions;

  if (includeDepartments) {
    const colDepartment: YColumnsType<T> = {
      title: 'Department',
      key: 'departmentName',
      dataIndex: ['department', 'name'],
      sorter: (a, b) => {
        if (a.department && b.department) {
          return a.department.name.localeCompare(b.department.name);
        } else {
          return 0;
        }
      },
    };
    columns.push(makeColumnFilterable<T>(colDepartment));
  }

  columns.push(
    {
      title: 'Status',
      dataIndex: ['evaluationSummary', 'status'],
      key: 'status',
      align: 'center',
      width: computeRemSize(110),
      render: translatePickingProgressStatus,
      defaultSortOrder: 'ascend',
      sorter: {
        compare: (a, b) => {
          return comparePickingProgressStatus(
            a.evaluationSummary.status,
            b.evaluationSummary.status,
          );
        },
        multiple: 2,
      },
      filters: [
        { text: 'Not Started', value: RouteExecutionStatus.NotStarted },
        { text: 'In progress', value: RouteExecutionStatus.InProgress },
        { text: 'Finished', value: RouteExecutionStatus.Finished },
      ],
      onFilter: (value, record) => record.evaluationSummary.status === value,
    },
    {
      title: `Proposed Value [${currency}]`,
      dataIndex: ['evaluationSummary', 'totalValue'],
      key: 'totalValue',
      align: 'right',
      width: computeRemSize(160),
      render: (value: number) => formatNumber(value, 0),
      sorter: (a, b) => a.evaluationSummary.totalValue - b.evaluationSummary.totalValue,
    },
    {
      title: `Shipped Value [${currency}]`,
      dataIndex: ['evaluationSummary', 'shippedValue'],
      key: 'shippedValue',
      align: 'right',
      width: computeRemSize(160),
      render: (value: number) => formatNumber(value, 0),
      sorter: (a, b) => a.evaluationSummary.shippedValue - b.evaluationSummary.shippedValue,
    },
    {
      title: `Shipped Value [%]`,
      dataIndex: ['evaluationSummary', 'shippedValueRate'],
      key: 'shippedValueRate',
      align: 'right',
      width: computeRemSize(160),
      render: (value: number) => formatPercentage(value * 100, 0),
      defaultSortOrder: 'ascend',
      sorter: {
        compare: (a, b) =>
          a.evaluationSummary.shippedValueRate - b.evaluationSummary.shippedValueRate,
        multiple: 1,
      },
    },
  );

  if (includeDeliveredValue) {
    columns.push(
      {
        title: `Delivered Value [${currency}]`,
        dataIndex: ['evaluationSummary', 'deliveredValue'],
        key: 'deliveredValue',
        align: 'right',
        width: computeRemSize(160),
        render: (value: number) => formatNumber(value, 0),
        sorter: (a, b) => a.evaluationSummary.deliveredValue - b.evaluationSummary.deliveredValue,
      },
      {
        title: `Delivered Value [%]`,
        dataIndex: ['evaluationSummary', 'deliveredValueRate'],
        key: 'deliveredValueRate',
        align: 'right',
        width: computeRemSize(160),
        render: (value: number) => formatPercentage(value * 100, 0),
        sorter: (a, b) =>
          a.evaluationSummary.deliveredValueRate - b.evaluationSummary.deliveredValueRate,
      },
    );
  }

  if (includeRoutes) {
    columns.push(
      {
        title: 'Total routes',
        dataIndex: ['evaluationSummary', 'totalRouteCount'],
        key: 'routeCount',
        align: 'right',
        width: computeRemSize(colWidth),
        render: (value: number) => formatNumber(value, 0),
        sorter: (a, b) => {
          if (
            'totalRouteCount' in a.evaluationSummary &&
            'totalRouteCount' in b.evaluationSummary
          ) {
            return a.evaluationSummary.totalRouteCount - b.evaluationSummary.totalRouteCount;
          } else {
            return 0;
          }
        },
      },
      {
        title: 'Remaining routes',
        key: 'remainingRouteCount',
        align: 'right',
        width: computeRemSize(colWidth),
        render: (record: T) => {
          if (
            'finishedRouteCount' in record.evaluationSummary &&
            'totalRouteCount' in record.evaluationSummary
          ) {
            return (
              record.evaluationSummary.totalRouteCount - record.evaluationSummary.finishedRouteCount
            );
          } else {
            return 0;
          }
        },
        sorter: (a, b) => {
          if (
            'finishedRouteCount' in a.evaluationSummary &&
            'totalRouteCount' in a.evaluationSummary &&
            'finishedRouteCount' in b.evaluationSummary &&
            'totalRouteCount' in b.evaluationSummary
          ) {
            const remainingA =
              a.evaluationSummary.totalRouteCount - a.evaluationSummary.finishedRouteCount;
            const remainingB =
              b.evaluationSummary.totalRouteCount - b.evaluationSummary.finishedRouteCount;
            return remainingA - remainingB;
          } else {
            return 0;
          }
        },
      },
    );
  }

  return columns;
};
